import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { CalendarOff, CalendarCheck, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function Availability() {
  const [weekdays, setWeekdays] = useState([0, 1, 2, 3, 4, 5, 6]);
  const [blocked, setBlocked] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/availability").then((r) => { setWeekdays(r.data.weekdays); setBlocked(r.data.blocked_dates); });
  }, []);

  const toggleDay = (d) => setWeekdays((w) => (w.includes(d) ? w.filter((x) => x !== d) : [...w, d].sort()));
  const blockedDates = blocked.map((d) => parseISO(d));
  const onSelectDates = (dates) => setBlocked((dates || []).map((d) => format(d, "yyyy-MM-dd")).sort());

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/availability", { weekdays, blocked_dates: blocked });
      toast.success("Availability saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="availability-page">
      <div>
        <h1 className="font-heading font-black text-3xl tracking-tight text-sapphire">Availability</h1>
        <p className="text-muted-foreground text-sm mt-1">Hosts can only book you on days you're available. Marketplace jobs on other days are hidden.</p>
      </div>

      <section className="border border-border rounded-md bg-white p-6 space-y-4" data-testid="weekly-schedule">
        <div className="flex items-center gap-2 text-sapphire"><CalendarCheck className="w-5 h-5 text-ocean" /><h2 className="font-heading font-bold text-lg">Weekly schedule</h2></div>
        <div className="flex flex-wrap gap-2">
          {WEEKDAYS.map((label, i) => {
            const on = weekdays.includes(i);
            return (
              <button key={label} type="button" onClick={() => toggleDay(i)} data-testid={`weekday-${label.toLowerCase()}`} aria-pressed={on}
                className={`px-4 py-2 rounded-md text-sm font-semibold border transition-colors ${on ? "bg-sapphire text-white border-sapphire" : "bg-white text-slate-500 border-border hover:border-sapphire"}`}>
                {label}
              </button>
            );
          })}
        </div>
        {weekdays.length === 0 && <p className="text-xs text-red-600">You have no available days — hosts won't be able to book you.</p>}
      </section>

      <section className="border border-border rounded-md bg-white p-6 space-y-4" data-testid="blocked-dates">
        <div className="flex items-center gap-2 text-sapphire"><CalendarOff className="w-5 h-5 text-ocean" /><h2 className="font-heading font-bold text-lg">Blocked dates</h2></div>
        <p className="text-sm text-muted-foreground">Click dates you're away (vacation, appointments). Click again to unblock.</p>
        <div className="grid md:grid-cols-2 gap-6">
          <Calendar mode="multiple" selected={blockedDates} onSelect={onSelectDates} className="border border-border rounded-md" data-testid="blocked-calendar" />
          <div className="space-y-2">
            {blocked.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-blocked">No blocked dates.</p>}
            {blocked.map((d) => (
              <div key={d} className="flex items-center justify-between bg-slate-50 border border-border rounded px-3 py-2 text-sm" data-testid={`blocked-${d}`}>
                <span>{format(parseISO(d), "EEE, MMM d yyyy")}</span>
                <button type="button" onClick={() => setBlocked(blocked.filter((x) => x !== d))} aria-label="Unblock"><X className="w-4 h-4 text-slate-500" /></button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Button onClick={save} disabled={saving} className="bg-sapphire text-white" data-testid="availability-save">
        {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Save availability
      </Button>
    </div>
  );
}
